import { gql, useQuery } from '@apollo/client';
import { validateZipCode } from '@perch-labs/warbler';
import { useEffect, useMemo } from 'react';
import { ErrorMessage } from '~/components/error-message';
import { LDClient, LDFlags } from '~/providers';
import { Flex_FetchUtilitiesQuery, Flex_FetchUtilitiesQueryVariables } from '~/typings/graphql';
import { ArrayElementType } from '~/typings/utils';
import { getStyledSystemProps } from '~/utils/styled-system';
import { StyledSelect, StyledSelectProps } from '../select.style';

export const FETCH_UTILITIES = gql`
  query Flex_FetchUtilities($zipCode: String!) {
    utilities: utilitiesByZip(zipCode: $zipCode) {
      suggested {
        id
        name: displayName
      }
    }
  }
`;

type UtilityData = {
  id: number;
  name: string;
} | null;

type UtilityType = NonNullable<
  ArrayElementType<Flex_FetchUtilitiesQuery['utilities']['suggested']>
>;
type Option = { text: string; value: number };

const mapOptions = (utility: UtilityType): Option => ({
  text: utility.name,
  value: utility.id,
});
interface Props extends StyledSelectProps {
  onChange: (zip: number, { isValid, name }: { isValid: boolean; name: 'utilityId' }) => void;
  value?: number;
  zipCode: string;
}

export const Utility = ({ onChange, value, zipCode, ...rest }: Props) => {
  const { data, loading } = useQuery<Flex_FetchUtilitiesQuery, Flex_FetchUtilitiesQueryVariables>(
    FETCH_UTILITIES,
    {
      skip: !validateZipCode(zipCode),
      variables: { zipCode },
    },
  );

  const { suggested } = useMemo(() => {
    const filterUtilities = (utilities: UtilityData[]) => {
      if (!LDClient.variation(LDFlags.enableUnitilMA))
        return utilities?.filter(utility => utility?.name !== 'Unitil MA');
      return utilities;
    };

    const suggestedUtilities: UtilityData[] = data?.utilities?.suggested || [];
    // removed filter for Unitil MA to test
    return {
      suggested: filterUtilities(suggestedUtilities),
    };
  }, [data?.utilities]);
  const availableUtilities = !!suggested?.length;

  const handleChange = (id: number | string) => {
    onChange(Number(id), { isValid: !!id, name: 'utilityId' });
  };

  useEffect(() => {
    // clear the value if fetchUtility changes. this is when user types
    // a zip, deletes it and puts another one. this component should
    // probably manage its own internal state for value. but until that
    // we need this.
    handleChange('');
    if (value) {
      handleChange(value);
    }
  }, [suggested]);

  const showError = !loading && validateZipCode(zipCode) && !suggested?.length;

  const error = showError ? 'No utilities available for the entered ZIP code.' : '';

  return (
    <>
      <StyledSelect
        {...getStyledSystemProps(rest)}
        disabled={!availableUtilities}
        id="utility-select"
        label="Select your electric utility"
        loading={loading}
        minHeight="auto"
        name="utilityId"
        onChange={handleChange}
        options={suggested?.map(mapOptions) || []}
        value={value ? String(value) : ''}
        placeholder="Please select your utility"
      />
      {!!showError && <ErrorMessage error={error} mb={2} allow />}
    </>
  );
};
